import { ClipboardDocumentIcon, CheckIcon, BookOpenIcon, DocumentIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { type Message, type Source, uploadedImageUrl } from '../../api/unimedica';
import { useRef } from 'react';
import ChatMarkdown from '../ChatMarkdown';

// Port aus chat/MessageBubble.svelte — Benutzer rechts (Sprechblase),
// Assistent links (Balken + Markdown + Quellen + Debug-Telemetrie).

export const fmtTime = (iso: string) => { try { return new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

export default function MessageBubble({ m, copiedId, showDeriveButton, debugMode, activeModel, onCopy, onSourceClick, onDeriveKnowledge }: {
  m: Message; copiedId: string | null; showDeriveButton: boolean; debugMode: boolean; activeModel?: string;
  onCopy: (id: string, content: string, el?: HTMLElement | null) => void; onSourceClick: (s: Source) => void; onDeriveKnowledge: (messageId: string) => void;
}) {
  const inhalt = useRef<HTMLDivElement>(null);   // gerenderte Antwort — wird formatiert kopiert (21.10.2026)
  const sources = m.sources ?? [];
  const imgSources = sources.filter((s: any): s is { image_id: string } => 'image_id' in s);
  const patientSources = sources.filter((s: any): s is { patient_id: string; filename: string } => 'filename' in s);
  const webSources = sources.filter((s: any): s is { url: string; title: string } => 'url' in s);
  const Kopie = ({ label }: { label?: string }) => (
    <button type="button" onClick={() => onCopy(m.id, m.content, inhalt.current)} title="Kopieren" aria-label="Kopieren"
      className="inline-flex min-h-9 items-center gap-1 rounded px-2 py-1 text-[10.5px] text-ps-dark-400 opacity-70 transition-opacity hover:bg-gray-50 hover:text-primary-500 group-hover/msg:opacity-100">
      {copiedId === m.id ? <><CheckIcon className="h-3 w-3" />{label && <span>kopiert</span>}</> : <><ClipboardDocumentIcon className="h-3 w-3" />{label && <span>{label}</span>}</>}
    </button>
  );

  if (m.role === 'user') return (
    <article className="group/msg relative flex flex-col items-end">
      <div className="mb-1.5 flex items-center gap-2 text-[11px] text-ps-dark-400">
        {m.content?.trim() && <Kopie />}
        <span>Du · <span className="numerical">{fmtTime(m.created_at)}</span></span>
      </div>
      {imgSources.length > 0 && (
        <div className="mb-1.5 flex max-w-[88%] flex-wrap justify-end gap-1.5">
          {imgSources.map((s) => (
            <a key={s.image_id} href={uploadedImageUrl(s.image_id)} target="_blank" rel="noopener" className="block overflow-hidden rounded-[6px] border border-primary-300 bg-white transition-opacity hover:opacity-90">
              <img src={uploadedImageUrl(s.image_id)} alt="Hochgeladenes Bild" className="block max-h-[180px] max-w-[280px] object-contain"
                onError={(e) => { const img = e.currentTarget; img.style.display = 'none'; const fb = img.nextElementSibling as HTMLElement | null; if (fb) fb.style.display = 'flex'; }} />
              <div className="hidden h-[120px] w-[200px] flex-col items-center justify-center bg-gray-50 px-4 text-center text-ps-dark-400">
                <div className="text-[11px] font-medium">Bild nicht mehr verfügbar</div><div className="mt-0.5 text-[10px] opacity-70">automatisch nach 24h gelöscht</div>
              </div>
            </a>
          ))}
        </div>
      )}
      {m.content?.trim() && <div className="max-w-[88%] whitespace-pre-wrap rounded-[8px] rounded-tr-[2px] border border-primary-300 bg-primary-50 px-4 py-3 text-[15px] leading-[1.55] text-ps-dark-900">{m.content}</div>}
    </article>
  );

  if (m.role !== 'assistant') return null;
  return (
    <article className="group/msg">
      <div className="mb-2 flex items-center gap-2 text-[11px]">
        <span className="font-semibold text-primary-500">KI-Assistent</span><span className="text-ps-dark-400">·</span>
        <span className="numerical text-ps-dark-400">{fmtTime(m.created_at)}</span>
        <Kopie label="kopieren" />
        {showDeriveButton && (
          <button type="button" onClick={() => onDeriveKnowledge(m.id)} title="Allgemein nachschlagen — ohne Patientendaten"
            className="inline-flex min-h-9 items-center gap-1 rounded px-2 py-1 text-[10.5px] text-ps-dark-400 opacity-70 transition-opacity hover:bg-emerald-50 hover:text-emerald-800 group-hover/msg:opacity-100">
            <BookOpenIcon className="h-3 w-3" /><span>Allgemein nachschlagen</span>
          </button>
        )}
      </div>
      <div ref={inhalt} className="border-l-2 border-primary-300 pl-4"><ChatMarkdown text={m.content} onSourceClick={onSourceClick} /></div>
      {(patientSources.length > 0 || webSources.length > 0) && (
        <div className="ml-4 mt-3 border-t border-dashed border-gray-200 pt-3">
          <div className="mb-2 text-[11px] font-medium text-ps-dark-500">Quellen:</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {patientSources.map((s, i) => (
              <button key={i} onClick={() => onSourceClick(s)} title="Patientenakte öffnen"
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] text-ps-dark-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-500">
                <DocumentIcon className="h-3 w-3" /><span className="min-w-0 truncate">{(s.filename ?? '').replace(/\.md$/, '').replace(/_/g, ' ')}</span>
              </button>
            ))}
            {webSources.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={s.url}
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] text-ps-dark-700 no-underline transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800">
                <GlobeAltIcon className="h-3 w-3" /><span className="min-w-0 truncate">{s.title ?? s.url}</span>
              </a>
            ))}
          </div>
        </div>
      )}
      {debugMode && (m.tokens_in || m.tokens_out || m.duration_ms) && (
        <div className="mt-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-dashed border-gray-200 bg-gray-50 px-2.5 py-1 font-mono text-[10.5px] text-ps-dark-400">
          <span title="Modell">{activeModel ?? '?'}</span>
          {(m.tokens_in != null || m.tokens_out != null) && <span title="Token-Verbrauch">↓{m.tokens_in ?? '?'} · ↑{m.tokens_out ?? '?'}</span>}
          {m.duration_ms != null && <span title="Antwort-Dauer">{m.duration_ms >= 1000 ? `${(m.duration_ms / 1000).toFixed(1)}s` : `${m.duration_ms}ms`}</span>}
          {m.tokens_out && m.duration_ms ? <span title="Tokens pro Sekunde">{(m.tokens_out / (m.duration_ms / 1000)).toFixed(1)} t/s</span> : null}
          {m.tool_calls && <span title="Tool-Aufrufe" className="text-primary-500">🔧 {Array.isArray(m.tool_calls) ? m.tool_calls.length : '1'}</span>}
        </div>
      )}
    </article>
  );
}
